import type { TradingStrategy } from '@einhaus/project314-types';
import type { SearchSelectItem } from './SearchSelect';

export interface TradingStrategySelectItemOptions {
    /** Removes strategies from the list entirely, e.g. ones the user cannot consume. */
    filter?: ((strategy: TradingStrategy) => boolean) | undefined;
    /**
     * Keeps a strategy listed but unselectable, labeled with the returned reason, e.g. "Already
     * subscribed". Returns null for strategies that can be chosen.
     */
    getUnavailableReason?: ((strategy: TradingStrategy) => string | null) | undefined;
}

export const toTradingStrategySelectItem = (strategy: TradingStrategy, unavailableReason: string | null = null): SearchSelectItem => {
    const name = strategy.name || `Strategy ${strategy.id}`;
    const label = `${name} (#${strategy.id})`;

    return {
        // The reason a strategy cannot be chosen matters more than its inactive state, and a row holds one badge.
        badge: unavailableReason ?? (strategy.active ? undefined : 'Inactive'),
        isDisabled: unavailableReason !== null,
        label: unavailableReason === null ? label : `${label}, ${unavailableReason.toLowerCase()}`,
        meta: strategy.description ? `${strategy.duration} · ${strategy.description}` : strategy.duration,
        name,
        searchText: `${strategy.duration} ${strategy.description}`.toLowerCase(),
        value: strategy.id
    };
};

/**
 * Select items for the given strategies. Unavailable strategies follow the selectable ones, each group
 * keeping its incoming order, so the choices a user can make come first while the rest stay findable.
 */
export const buildTradingStrategySelectItems = (
    strategies: readonly TradingStrategy[],
    { filter, getUnavailableReason }: TradingStrategySelectItemOptions = {}
): SearchSelectItem[] => {
    const selectableItems: SearchSelectItem[] = [];
    const unavailableItems: SearchSelectItem[] = [];

    for (const strategy of strategies) {
        if (filter && !filter(strategy)) continue;

        const unavailableReason = getUnavailableReason?.(strategy) ?? null;

        if (unavailableReason === null) {
            selectableItems.push(toTradingStrategySelectItem(strategy));
        } else {
            unavailableItems.push(toTradingStrategySelectItem(strategy, unavailableReason));
        }
    }

    return [...selectableItems, ...unavailableItems];
};
